import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";
import {
  useDriverServiceV1GetDriverDetailQuery,
  useDriverServiceV1GetDriverDocumentsQuery,
} from "_services/modules/transport-resource/driver";
import { ExtendedDriverDetailRes, DriverDocumentItem } from "_interfaces/driver.interfaces";
import { useAuditLogServiceV1ListAuditLogsQuery } from "_services/modules/audit-log/audit-log";
import type { AuditLogItem } from "_interfaces/audit-log.interfaces";
import { useGetDriverTransportOrderHistoryQuery } from "_services/modules/transport-operation/transport-order";

export interface TransportOrderRow {
  id: string;
  wo_number: string;
  customer_name: string;
  date: string;
  fund_transferred: number;
  status: string;
}

const getErrorMessage = (error: unknown): string | undefined => {
  if (!error) return undefined;
  if (typeof error === "object" && error !== null) {
    if ("data" in error && typeof (error as { data?: unknown }).data === "object") {
      const data = (error as { data?: { message?: unknown } }).data;
      if (typeof data?.message === "string") return data.message;
    }
    if ("message" in error && typeof (error as { message?: unknown }).message === "string") {
      return (error as { message: string }).message;
    }
  }
  return "Failed to load transport order history";
};

const useDriverDetail = () => {
  const dispatch = useAppDispatch();
  const { id = "" } = useParams();
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<
    "transport-order" | "document-list"
  >("transport-order");
  const [toPage, setToPage] = useState(1);
  const [toLimit, setToLimit] = useState(10);

  const {
    data,
    isLoading: isDetailLoading,
    refetch,
  } = useDriverServiceV1GetDriverDetailQuery(
    { id: String(id) },
    { skip: !id, refetchOnMountOrArgChange: true }
  );

  const { data: documentsData, isLoading: isDocumentsLoading } =
    useDriverServiceV1GetDriverDocumentsQuery(
      { id: String(id) },
      { skip: !id, refetchOnMountOrArgChange: true }
    );

  const { data: auditLogs, isLoading: isAuditLoading } = useAuditLogServiceV1ListAuditLogsQuery(
    {
      entityType: "DRIVER",
      entityId: id,
      page: 1,
      size: 20,
    },
    {
      skip: !id,
    }
  );

  const {
    data: toHistoryData,
    isLoading: isTOHistoryLoading,
    isFetching: isTOHistoryFetching,
    error: toHistoryError,
  } = useGetDriverTransportOrderHistoryQuery(
    {
      driverId: String(id),
      page: toPage,
      limit: toLimit,
    },
    { skip: !id }
  );

  useEffect(() => {
    dispatch(
      setGlobalComponent({
        title: "Detail Driver",
        hasBackButton: true,
      })
    );
  }, [dispatch]);

  const detail = data as ExtendedDriverDetailRes | undefined;
  const docsList: DriverDocumentItem[] | undefined = detail?.documents || documentsData?.documents;

  const transportOrders: TransportOrderRow[] = useMemo(() => {
    return (
      toHistoryData?.items?.map((item) => ({
        id: item.id,
        wo_number: item.wo_number,
        customer_name: item.customer_name,
        date: item.date,
        fund_transferred: item.fund_transferred,
        status: item.status,
      })) ?? []
    );
  }, [toHistoryData?.items]);

  const toTotalPages = toHistoryData?.meta?.total_page ?? 1;

  const handleTOPageChange = (newPage: number) => {
    setToPage(newPage);
  };

  const handleTOLimitChange = (newLimit: number) => {
    setToLimit(newLimit);
    setToPage(1);
  };

  return {
    id,
    detail,
    documents:
      docsList?.map((item) => ({
        id: item.id ?? "",
        type: item.document_type || item.type || "",
        file_url: item.document_url || item.file_url || "",
        created_at: item.created_at ?? "",
        expiry_date: item.expiry_date ?? "",
      })) ?? [],
    auditLogs: (auditLogs?.audit_logs as AuditLogItem[] | undefined) ?? [],
    isHistoryOpen,
    setIsHistoryOpen,
    activeTab,
    setActiveTab,
    isLoading: isDetailLoading || isDocumentsLoading || isAuditLoading,
    transportOrders,
    isTOHistoryLoading,
    isTOHistoryFetching,
    toErrorMessage: getErrorMessage(toHistoryError),
    toPage,
    toLimit,
    toTotalPages,
    handleTOPageChange,
    handleTOLimitChange,
    refetch,
  };
};

export default useDriverDetail;
